import Model, { belongsTo, attr } from '@ember-data/model'
import { service } from '@ember/service'
import { Type } from '@warp-drive/core-types/symbols'
import type { IntlService } from 'ember-intl'
import type School from 'district-ui-client/models/school'
import type { SubscriptionType } from 'district-ui-client/domain/subscription-type'
import type DateTransform from 'district-ui-client/transforms/date'
import type NumberTransform from 'district-ui-client/transforms/number'
import type StringTransform from 'district-ui-client/transforms/string'

export const LICENCE_COUNT_UNLIMITED = 0

/**
 * Unlike school subscriptions, these sub utilisations are virtual records, they indicate the usage of a subscription
 * type (reading, maths or writing).
 *
 * If a utilisation record is present for a subscription type, it means that school has active access to that product.
 * The school may have multiple school-subscriptions overlapping, and the utilisation represents a summation of those
 * capacities.
 */
export default class SchoolSubscriptionUtilisation extends Model {
  @service intl!: IntlService

  @attr<DateTransform>('date') declare startDate: Date

  @attr<DateTransform>('date') declare endDate: Date

  /** the total licence capacity of the subscription (0 if unlimited) */
  @attr<NumberTransform>('number') declare licenceCount: number
  /** the number of active licences - ie active students */
  @attr<NumberTransform>('number') declare studentCount: number

  @attr<StringTransform>('string') declare subscriptionType: SubscriptionType

  @belongsTo<School>('school', { async: false, inverse: 'schoolSubscriptionUtilisations' }) declare school: School

  get isUnlimitedCapacity() {
    // Ensure both - If it's unlimited, it shouldn't ever be one or the other.
    return this.licenceCount === LICENCE_COUNT_UNLIMITED
  }

  get isOverCapacity() {
    return !this.isUnlimitedCapacity && this.studentCount > this.licenceCount
  }

  get formattedCapacity() {
    const licenceCount = this.isUnlimitedCapacity ? this.intl.t('unlimited') : this.licenceCount
    return `${this.studentCount} / ${licenceCount}`
  }

  [Type] = 'school-subscription-utilisation' as const
}
